import { FleetScheduleVehicleSwapBlockedReason } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiIcon } from 'noddi-ui-common';
import {
  ApiErrorMessage,
  ErrorCodes,
  NoddiBadge,
  NoddiButton,
  NoddiInfoBanner,
  NoddiSelectableBox,
  NoddiSpinner
} from 'noddi-ui-web';
import { CountryCode, formatLicensePlate } from 'noddi-util';
import { FleetSwapTranslations } from './fleetSwapTranslations';
import { FleetSwapCandidate, FleetSwapScope } from './fleetSwapTypes';

type Props = {
  scope: FleetSwapScope;
  /** Complete plate typed into the swap search that matched no car in the fleet. */
  licensePlateNumber: string;
  isSelected: boolean;
  onSelect: (candidate: FleetSwapCandidate | null) => void;
  translations: FleetSwapTranslations;
  errorCodes?: ErrorCodes;
};

/** Offers to look a plate up in the vehicle registry, then says whether it can take the schedule over. */
export const FleetSwapRegistryCard = ({
  scope,
  licensePlateNumber,
  isSelected,
  onSelect,
  translations: t,
  errorCodes
}: Props) => {
  const {
    data: car,
    error,
    refetch: lookUp,
    isFetching
  } = noddiAsync.useGet({
    type: URLKeys.getCarFromLicensePlateNumber,
    input: {
      number: licensePlateNumber,
      countryCode: CountryCode.NORWAY,
      brandDomains: scope.kind === 'customer' ? [scope.brandDomain] : undefined
    },
    queryConfig: { enabled: false }
  });

  if (car) {
    return (
      <FoundCar
        scope={scope}
        candidate={{
          carId: car.id,
          licensePlateNumber: car.licensePlateNumber,
          carDisplayName: [car.makeName, car.model].filter(Boolean).join(' ') || null,
          isNewToFleet: true
        }}
        isSelected={isSelected}
        onSelect={onSelect}
        translations={t}
        errorCodes={errorCodes}
      />
    );
  }

  return (
    <div className='flex flex-col gap-3 rounded-lg border border-border bg-muted p-4'>
      <div className='flex items-center gap-4'>
        <div className='flex min-w-0 flex-1 flex-col'>
          <span className='text-sm font-semibold'>{t.notInFleet(licensePlateNumber)}</span>
          <span className='text-xs text-muted-foreground'>{t.lookUpHint}</span>
        </div>
        <NoddiButton
          size='small'
          startIcon='Search'
          onClick={() => void lookUp()}
          loading={isFetching}
          disabled={isFetching}
        >
          {t.lookUpCar}
        </NoddiButton>
      </div>
      {error && <ApiErrorMessage error={error} errorCodes={errorCodes} />}
    </div>
  );
};

type FoundCarProps = Omit<Props, 'licensePlateNumber'> & { candidate: FleetSwapCandidate };

const FoundCar = ({ scope, candidate, isSelected, onSelect, translations: t, errorCodes }: FoundCarProps) => {
  const plate = formatLicensePlate(candidate.licensePlateNumber);

  // Customers may not take over another customer's car; partners may, so they only need the
  // enrollment check -- a car can be on one schedule at a time either way.
  const customerCheck = noddiAsync.useGet({
    type: URLKeys.getUserGroupFleetScheduleVehicleSwapCandidate,
    input: { userGroupId: scope.userGroupId, carId: candidate.carId },
    queryConfig: { enabled: scope.kind === 'customer' }
  });
  const partnerEnrollments = noddiAsync.useGet({
    type: URLKeys.getPaginatedFleetScheduleVehiclesAll,
    input: { carIds: [candidate.carId], pageSize: 1 },
    queryConfig: { enabled: scope.kind === 'partner' }
  });
  const { isPending, error } = scope.kind === 'customer' ? customerCheck : partnerEnrollments;

  if (error) {
    return <ApiErrorMessage error={error} errorCodes={errorCodes} />;
  }
  if (isPending) {
    return (
      <div className='flex justify-center py-4'>
        <NoddiSpinner />
      </div>
    );
  }

  const blockedReason =
    scope.kind === 'customer'
      ? customerCheck.data?.blockedReason
      : (partnerEnrollments.data?.count ?? 0) > 0
        ? FleetScheduleVehicleSwapBlockedReason.ALREADY_ENROLLED
        : null;
  if (blockedReason) {
    return (
      <NoddiInfoBanner
        variant='warning'
        icon='TriangleAlert'
        title={t.cannotBeAdded(plate)}
        texts={[
          {
            value:
              blockedReason === FleetScheduleVehicleSwapBlockedReason.OWNED_BY_OTHER_CUSTOMER
                ? t.ownedByOtherCustomer(plate)
                : t.alreadyOnSchedule(plate)
          }
        ]}
      />
    );
  }

  return (
    <NoddiSelectableBox
      size='small'
      isSelected={isSelected}
      setIsSelected={(next) => onSelect(next ? candidate : null)}
    >
      <div className='flex items-center gap-3'>
        <NoddiIcon name='Car' size='small' className='shrink-0 text-primary' />
        <div className='flex min-w-0 flex-1 flex-col'>
          <span className='text-sm font-medium tracking-wider tabular-nums'>{plate}</span>
          {candidate.carDisplayName && (
            <span className='truncate text-xs text-muted-foreground'>{candidate.carDisplayName}</span>
          )}
        </div>
        <NoddiBadge variant='outline'>{t.foundInRegistry}</NoddiBadge>
      </div>
      <p className='mt-3 flex items-center gap-2 border-t border-border pt-3 text-xs text-muted-foreground'>
        <NoddiIcon name='Info' size='xSmall' className='shrink-0' />
        {t.addedOnConfirm(plate)}
      </p>
    </NoddiSelectableBox>
  );
};
